import { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ArrowLeft, User, Phone, Mail, MapPin, Calendar, TrendingDown, ShoppingCart, CreditCard, Clock, Save, Building2, DollarSign, TrendingUp, AlertCircle, Wallet, Package, Percent, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import { format, startOfDay, endOfDay } from "date-fns";
import { ptBR } from "date-fns/locale";
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { logCrud } from "@/services/activities";

type Supplier = {
  id: string;
  nome: string;
  cnpj_cpf: string | null;
  contato: string | null;
  telefone: string | null;
  whatsapp: string | null;
  email: string | null;
  endereco_completo: string | null;
  created_at: string;
};

type Transaction = {
  id: string;
  tipo: string;
  valor_total: number;
  valor_liquido: number;
  desconto: number;
  status: string;
  created_at: string;
  descricao: string | null;
  categoria: string | null;
  expense_category_id: string | null;
  pagamento_json?: any;
};

type SupplierStats = {
  totalSpent: number;
  totalExpenses: number;
  averageExpense: number;
  lastExpense: string | null;
  frequency: number;
  topCategories: { category: string; value: number; count: number }[];
  monthlySpending: { month: string; value: number }[];
  paymentMethods: { method: string; value: number }[];
  totalPaid: number;
  totalPending: number;
  totalDiscount: number;
  averageDiscount: number;
  discountPercentage: number;
  categories: { category: string; value: number; count: number }[];
  bankAccounts: { account: string; value: number }[];
  paymentStatus: { status: string; value: number; count: number }[];
  weeklyDistribution: { day: string; value: number; count: number }[];
  growthTrend: number;
  averageDaysBetweenExpenses: number;
};

const extractDigits = (value: string) => value.replace(/\D/g, "");

const normalizePhoneDigits = (value: string) => {
  if (!value) return "";
  let digits = extractDigits(value);
  if (!digits) return "";
  if (digits.startsWith("55") && digits.length > 11) {
    digits = digits.slice(2);
  }
  return digits.slice(0, 11);
};

const formatPhoneBR = (value: string | null | undefined) => {
  const digits = normalizePhoneDigits(value || "");
  if (!digits) return "";

  if (digits.length <= 10) {
    return digits
      .replace(/^(\d{0,2})(\d)/, "($1) $2")
      .replace(/(\d{4})(\d)/, "$1-$2")
      .slice(0, 14);
  }

  return digits
    .replace(/^(\d{0,2})(\d)/, "($1) $2")
    .replace(/(\d{5})(\d)/, "$1-$2")
    .slice(0, 15);
};

const formatCpfCnpj = (value: string | null | undefined) => {
  const digits = extractDigits((value || "").slice(0, 14));
  if (!digits) return "";

  if (digits.length <= 11) {
    return digits
      .replace(/^(\d{0,3})(\d)/, "$1.$2")
      .replace(/^(\d{3}\.\d{0,3})(\d)/, "$1.$2")
      .replace(/^(\d{3}\.\d{3}\.\d{0,3})(\d{0,2})$/, "$1-$2")
      .slice(0, 14);
  }

  return digits
    .padEnd(14, "")
    .replace(/^(\d{0,2})(\d)/, "$1.$2")
    .replace(/^(\d{2}\.\d{0,3})(\d)/, "$1.$2")
    .replace(/^(\d{2}\.\d{3}\.\d{0,3})(\d)/, "$1/$2")
    .replace(/^(\d{2}\.\d{3}\.\d{3}\/\d{0,4})(\d{0,2})$/, "$1-$2")
    .slice(0, 18);
};

const sanitizeDigits = (value: FormDataEntryValue | null) => {
  if (!value) return null;
  const digits = extractDigits(String(value));
  return digits ? digits : null;
};

const sanitizePhone = (value: FormDataEntryValue | null) => {
  const digits = normalizePhoneDigits(String(value ?? ""));
  return digits ? digits : null;
};

const formatCurrency = (value: number | null | undefined): string => {
  if (value === null || value === undefined || isNaN(value)) return "R$ 0,00";
  return new Intl.NumberFormat("pt-BR", { 
    style: "currency", 
    currency: "BRL",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
};

export default function SupplierDetails(props?: { id?: string; embedded?: boolean; periodRange?: [Date, Date]; setLoading?: (loading: boolean) => void }) {
  const { id: routeId } = useParams<{ id: string }>();
  const id = props?.id ?? routeId;
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const search = new URLSearchParams(location.search);
  const isEmbedded = (typeof props?.embedded !== 'undefined') ? !!props?.embedded : (search.get("embedded") === "1");
  const fromStr = search.get("from");
  const toStr = search.get("to");
  const [cadFrom, setCadFrom] = useState<Date>(props?.periodRange ? startOfDay(props.periodRange[0]) : (fromStr ? startOfDay(new Date(fromStr)) : startOfDay(new Date(0))));
  const [cadTo, setCadTo] = useState<Date>(props?.periodRange ? endOfDay(props.periodRange[1]) : (toStr ? endOfDay(new Date(toStr)) : endOfDay(new Date())));
  useEffect(() => {
    if (isEmbedded && !props?.periodRange) {
      const handler = (e: MessageEvent) => {
        const data = (e.data || {}) as any;
        if (data && data.type === 'rx-period-change') {
          setCadFrom(startOfDay(new Date(data.from)));
          setCadTo(endOfDay(new Date(data.to)));
        }
      };
      window.addEventListener('message', handler);
      return () => window.removeEventListener('message', handler);
    }
  }, [isEmbedded, props?.periodRange]);

  const { data: supplier, isLoading: supplierLoading } = useQuery({
    queryKey: ["supplier", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("suppliers")
        .select("*")
        .eq("id", id)
        .single();
      if (error) throw error;
      return data as Supplier;
    },
    enabled: !!id,
  });

  const { data: transactions, isLoading: transactionsLoading } = useQuery({
    queryKey: ["supplier-transactions", id, cadFrom.toISOString(), cadTo.toISOString()],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("transactions")
        .select("*")
        .eq("supplier_id", id)
        .eq("tipo", "saida") // Apenas despesas
        .gte("created_at", cadFrom.toISOString())
        .lte("created_at", cadTo.toISOString())
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data || []) as any[];
    },
    enabled: !!id,
  });

  useEffect(() => {
    const loading = supplierLoading || transactionsLoading;
    if (isEmbedded) {
      window.parent?.postMessage({ type: 'rx-loading', loading }, '*');
    }
    if (props?.setLoading) props.setLoading(loading);
  }, [supplierLoading, transactionsLoading, isEmbedded, props?.setLoading]);

  const { data: stats } = useQuery({
    queryKey: ["supplier-stats", id],
    queryFn: async () => {
      if (!transactions) return null;

      // Todas as transações já são despesas (tipo "saida")
      const expenses = transactions;
      const totalSpent = expenses.reduce((sum, t) => sum + Number(t.valor_liquido), 0);
      const totalExpenses = expenses.length;
      const averageExpense = totalExpenses > 0 ? totalSpent / totalExpenses : 0;
      const lastExpense = expenses.length > 0 ? expenses[0].created_at : null;

      // Calcular frequência (despesas nos últimos 30 dias)
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const frequency = expenses.filter(t => new Date(t.created_at) >= thirtyDaysAgo).length;

      // Top categorias de despesas
      const categoryMap = new Map<string, { value: number; count: number }>();
      expenses.forEach(expense => {
        const category = expense.categoria || 'Sem categoria';
        const current = categoryMap.get(category) || { value: 0, count: 0 };
        categoryMap.set(category, {
          value: current.value + Number(expense.valor_liquido),
          count: current.count + 1
        });
      });
      const topCategories = Array.from(categoryMap.entries())
        .map(([category, data]) => ({ category, ...data }))
        .sort((a, b) => b.value - a.value)
        .slice(0, 5);

      // Gastos mensais (últimos 6 meses)
      const monthlyMap = new Map<string, number>();
      expenses.forEach(expense => {
        const month = format(new Date(expense.created_at), "MMM/yy", { locale: ptBR });
        monthlyMap.set(month, (monthlyMap.get(month) || 0) + Number(expense.valor_liquido));
      });
      const monthlySpending = Array.from(monthlyMap.entries())
        .map(([month, value]) => ({ month, value }))
        .slice(-6);

      // Métodos de pagamento e status de pagamentos
      const expenseIds = expenses.map(s => s.id);
      let paymentMethods: { method: string; value: number }[] = [];
      let totalPaid = 0;
      let totalPending = 0;
      let bankAccounts: { account: string; value: number }[] = [];
      let paymentStatus: { status: string; value: number; count: number }[] = [];
      
      if (expenseIds.length > 0) {
        const { data: pays } = await supabase
          .from('transaction_payments')
          .select('payment_method_id, valor, status, transaction_id, bank_account_id')
          .in('transaction_id', expenseIds);

        const { data: pmRef } = await supabase
          .from('payment_methods')
          .select('id, nome');
        const nameById = new Map<string, string>();
        (pmRef || []).forEach((m: any) => nameById.set(m.id, m.nome));

        const { data: bankRef } = await supabase
          .from('bank_accounts')
          .select('id, nome');
        const bankNameById = new Map<string, string>();
        (bankRef || []).forEach((b: any) => bankNameById.set(b.id, b.nome));

        const paymentMap = new Map<string, number>();
        const bankMap = new Map<string, number>();
        const statusMap = new Map<string, { value: number; count: number }>();

        (pays || []).forEach((p: any) => {
          const id = p?.payment_method_id as string | null;
          const method = id ? (nameById.get(id) || id) : 'Não especificado';
          const valor = Number(p?.valor || 0);
          const status = p?.status || 'pendente';
          
          if (status === 'confirmado') {
            totalPaid += valor;
            paymentMap.set(method, (paymentMap.get(method) || 0) + valor);
          } else {
            totalPending += valor;
          }

          // Status de pagamento
          const currentStatus = statusMap.get(status) || { value: 0, count: 0 };
          statusMap.set(status, {
            value: currentStatus.value + valor,
            count: currentStatus.count + 1
          });

          // Contas bancárias
          const bankId = p?.bank_account_id as string | null;
          if (bankId) {
            const bankName = bankNameById.get(bankId) || 'Conta Desconhecida';
            bankMap.set(bankName, (bankMap.get(bankName) || 0) + valor);
          }
        });
        
        paymentMethods = Array.from(paymentMap.entries()).map(([method, value]) => ({ method, value }));
        bankAccounts = Array.from(bankMap.entries()).map(([account, value]) => ({ account, value }));
        paymentStatus = Array.from(statusMap.entries()).map(([status, data]) => ({ 
          status: status === 'confirmado' ? 'Confirmado' : 'Pendente', 
          ...data 
        }));
      }

      // Categorias de despesas (já calculado acima, mas vamos manter para compatibilidade)
      const categories = Array.from(categoryMap.entries())
        .map(([category, data]) => ({ category, ...data }))
        .sort((a, b) => b.value - a.value);

      // Descontos
      const totalDiscount = expenses.reduce((sum, t) => sum + Number(t.desconto || 0), 0);
      const averageDiscount = totalExpenses > 0 ? totalDiscount / totalExpenses : 0;
      const discountPercentage = totalSpent > 0 ? (totalDiscount / (totalSpent + totalDiscount)) * 100 : 0;

      // Distribuição semanal
      const weekDays = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
      const weeklyMap = new Map<string, { value: number; count: number }>();
      expenses.forEach(expense => {
        const dayIndex = new Date(expense.created_at).getDay();
        const day = weekDays[dayIndex];
        const current = weeklyMap.get(day) || { value: 0, count: 0 };
        weeklyMap.set(day, {
          value: current.value + Number(expense.valor_liquido),
          count: current.count + 1
        });
      });
      const weeklyDistribution = weekDays.map(day => {
        const data = weeklyMap.get(day) || { value: 0, count: 0 };
        return { day, ...data };
      });

      // Tendência de crescimento (comparar últimos 3 meses com 3 meses anteriores)
      const now = new Date();
      const threeMonthsAgo = new Date(now);
      threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
      const sixMonthsAgo = new Date(now);
      sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);

      const recentExpenses = expenses.filter(p => new Date(p.created_at) >= threeMonthsAgo);
      const olderExpenses = expenses.filter(p => {
        const date = new Date(p.created_at);
        return date >= sixMonthsAgo && date < threeMonthsAgo;
      });

      const recentTotal = recentExpenses.reduce((sum, p) => sum + Number(p.valor_liquido), 0);
      const olderTotal = olderExpenses.reduce((sum, p) => sum + Number(p.valor_liquido), 0);
      const growthTrend = olderTotal > 0 ? ((recentTotal - olderTotal) / olderTotal) * 100 : 0;

      // Média de dias entre despesas
      let averageDaysBetweenExpenses = 0;
      if (expenses.length > 1) {
        const sortedExpenses = [...expenses].sort((a, b) => 
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        );
        let totalDays = 0;
        for (let i = 1; i < sortedExpenses.length; i++) {
          const days = (new Date(sortedExpenses[i].created_at).getTime() - 
                       new Date(sortedExpenses[i-1].created_at).getTime()) / (1000 * 60 * 60 * 24);
          totalDays += days;
        }
        averageDaysBetweenExpenses = totalDays / (sortedExpenses.length - 1);
      }

      return {
        totalSpent,
        totalExpenses,
        averageExpense,
        lastExpense,
        frequency,
        topCategories,
        monthlySpending,
        paymentMethods,
        totalPaid,
        totalPending,
        totalDiscount,
        averageDiscount,
        discountPercentage,
        categories,
        bankAccounts,
        paymentStatus,
        weeklyDistribution,
        growthTrend,
        averageDaysBetweenExpenses,
      } as SupplierStats;
    },
    enabled: !!transactions,
  });

  const updateMutation = useMutation({
    mutationFn: async (formData: FormData) => {
      const { error } = await supabase
        .from("suppliers")
        .update({
          nome: formData.get("nome") as string,
          cnpj_cpf: sanitizeDigits(formData.get("cnpj_cpf")),
          contato: (formData.get("contato") as string) || null,
          telefone: sanitizePhone(formData.get("telefone")),
          whatsapp: sanitizePhone(formData.get("whatsapp")),
          email: (formData.get("email") as string) || null,
          endereco_completo: (formData.get("endereco_completo") as string) || null,
        })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["supplier", id] });
      queryClient.invalidateQueries({ queryKey: ["suppliers"] });
      toast.success("Fornecedor atualizado com sucesso");
      if (id) {
        void logCrud({ entitySlug: "entity:suppliers", operationSlug: "update", entityId: id, screenSlug: "fornecedor" });
      }
      setIsEditing(false);
    },
    onError: () => toast.error("Erro ao atualizar fornecedor"),
  });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    updateMutation.mutate(formData);
  };

  if (supplierLoading) {
    return <div className="p-8">Carregando...</div>;
  }

  if (!supplier) {
    return <div className="p-8">Fornecedor não encontrado</div>;
  }

  const COLORS = ['hsl(var(--primary))', 'hsl(var(--secondary))', 'hsl(var(--accent))', 'hsl(var(--muted))', 'hsl(var(--card))'];

  return (
    <div className="space-y-6">
      {/* Header */}
      {!isEmbedded && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate("/fornecedores")}> 
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold">{supplier.nome}</h1>
              <p className="text-muted-foreground">
                Fornecedor desde {format(new Date(supplier.created_at), "dd/MM/yyyy")}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* KPIs Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Gasto</CardTitle>
            <TrendingDown className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats ? `R$ ${stats.totalSpent.toFixed(2)}` : "-"}
            </div>
            {stats && stats.growthTrend !== 0 && (
              <p className={`text-xs mt-1 ${stats.growthTrend > 0 ? 'text-red-600' : 'text-green-600'}`}>
                {stats.growthTrend > 0 ? '↑' : '↓'} {Math.abs(stats.growthTrend).toFixed(1)}% vs período anterior
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Valor Pago</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats ? `R$ ${stats.totalPaid.toFixed(2)}` : "-"}
            </div>
            {stats && stats.totalPending > 0 && (
              <p className="text-xs text-amber-600 mt-1">
                R$ {stats.totalPending.toFixed(2)} pendente
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Despesa Média</CardTitle>
            <ShoppingCart className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats ? `R$ ${stats.averageExpense.toFixed(2)}` : "-"}
            </div>
            {stats && stats.averageDiscount > 0 && (
              <p className="text-xs text-muted-foreground mt-1">
                Desconto médio: R$ {stats.averageDiscount.toFixed(2)}
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total de Despesas</CardTitle>
            <CreditCard className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats?.totalExpenses || 0}
            </div>
            {stats && stats.frequency > 0 && (
              <p className="text-xs text-muted-foreground mt-1">
                {stats.frequency} nos últimos 30 dias
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Valor Pendente</CardTitle>
            <AlertCircle className="h-4 w-4 text-amber-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-amber-600">
              {stats ? `R$ ${stats.totalPending.toFixed(2)}` : "-"}
            </div>
            {stats && stats.paymentStatus.length > 0 && (
              <p className="text-xs text-muted-foreground mt-1">
                {stats.paymentStatus.find(s => s.status === 'Pendente')?.count || 0} pagamento(s) pendente(s)
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Desconto</CardTitle>
            <Percent className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats ? `R$ ${stats.totalDiscount.toFixed(2)}` : "-"}
            </div>
            {stats && stats.discountPercentage > 0 && (
              <p className="text-xs text-muted-foreground mt-1">
                {stats.discountPercentage.toFixed(1)}% do total
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Categorias Utilizadas</CardTitle>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats?.categories.length || 0}
            </div>
            {stats && stats.topCategories.length > 0 && (
              <p className="text-xs text-muted-foreground mt-1">
                Top: {stats.topCategories[0]?.category || "-"}
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Última Despesa</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats?.lastExpense 
                ? format(new Date(stats.lastExpense), "dd/MM/yyyy")
                : "Nunca"}
            </div>
            {stats && stats.averageDaysBetweenExpenses > 0 && (
              <p className="text-xs text-muted-foreground mt-1">
                Média: {Math.round(stats.averageDaysBetweenExpenses)} dias entre despesas
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Tabs Content */}
      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="overview">Visão Geral</TabsTrigger>
          <TabsTrigger value="expenses">Despesas</TabsTrigger>
          <TabsTrigger value="data">Dados Cadastrais</TabsTrigger>
          <TabsTrigger value="timeline">Timeline</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
        </TabsList>

        {/* Visão Geral */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Informações Básicas */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5" />
                  Informações de Contato
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {supplier.contato && (
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <span>{supplier.contato}</span>
                  </div>
                )}
                {supplier.telefone && (
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <span>{formatPhoneBR(supplier.telefone)}</span>
                  </div>
                )}
                {supplier.whatsapp && (
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <span>{formatPhoneBR(supplier.whatsapp)} (WhatsApp)</span>
                  </div>
                )}
                {supplier.email && (
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <span>{supplier.email}</span>
                  </div>
                )}
                {supplier.endereco_completo && (
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    <span>{supplier.endereco_completo}</span>
                  </div>
                )}
                {supplier.cnpj_cpf && (
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground">CNPJ/CPF:</span>
                    <span>{formatCpfCnpj(supplier.cnpj_cpf)}</span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Top Categorias */}
            <Card>
              <CardHeader>
                <CardTitle>Top 5 Categorias de Despesas</CardTitle>
              </CardHeader>
              <CardContent>
                {stats?.topCategories && stats.topCategories.length > 0 ? (
                  <div className="space-y-3">
                    {stats.topCategories.map((category, index) => (
                      <div key={index} className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{category.category}</p>
                          <p className="text-sm text-muted-foreground">{category.count} despesa(s)</p>
                        </div>
                        <Badge variant="secondary">
                          R$ {category.value.toFixed(2)}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-4">Nenhuma despesa registrada</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Gráficos */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Gastos Mensais */}
            <Card>
              <CardHeader>
                <CardTitle>Evolução de Gastos</CardTitle>
              </CardHeader>
              <CardContent>
                {stats?.monthlySpending && stats.monthlySpending.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={stats.monthlySpending}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip 
                        formatter={(value: number) => formatCurrency(value)}
                      />
                      <Line type="monotone" dataKey="value" stroke="hsl(var(--primary))" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-muted-foreground text-center py-12">Sem dados de gastos</p>
                )}
              </CardContent>
            </Card>

            {/* Métodos de Pagamento */}
            <Card>
              <CardHeader>
                <CardTitle>Métodos de Pagamento</CardTitle>
              </CardHeader>
              <CardContent>
                {stats?.paymentMethods && stats.paymentMethods.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={stats.paymentMethods}
                        dataKey="value"
                        nameKey="method"
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        label
                      >
                        {stats.paymentMethods.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-muted-foreground text-center py-12">Sem dados de pagamento</p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Despesas */}
        <TabsContent value="expenses" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Histórico de Despesas</CardTitle>
            </CardHeader>
            <CardContent>
              {transactionsLoading ? (
                <p className="text-center py-8">Carregando...</p>
              ) : transactions && transactions.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Data</TableHead>
                      <TableHead>Categoria</TableHead>
                      <TableHead>Descrição</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Valor</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {transactions.map((transaction) => (
                      <TableRow key={transaction.id}>
                        <TableCell>
                          {format(new Date(transaction.created_at), "dd/MM/yyyy HH:mm")}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {transaction.categoria || "Sem categoria"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {transaction.descricao || "-"}
                        </TableCell>
                        <TableCell>
                          <Badge variant={transaction.status === "paga" ? "default" : "outline"}>
                            {transaction.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          R$ {Number(transaction.valor_liquido).toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <p className="text-muted-foreground text-center py-8">Nenhuma despesa registrada</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Dados Cadastrais */}
        <TabsContent value="data" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Dados Cadastrais</CardTitle>
              {!isEditing && (
                <Button variant="outline" onClick={() => setIsEditing(true)}>
                  Editar
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {isEditing ? (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="nome">Nome/Razão Social *</Label>
                      <Input id="nome" name="nome" defaultValue={supplier.nome} required />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="cnpj_cpf">CNPJ/CPF</Label>
                      <Input 
                        id="cnpj_cpf" 
                        name="cnpj_cpf" 
                        defaultValue={formatCpfCnpj(supplier.cnpj_cpf || "")}
                        maxLength={18}
                        onChange={(event) => {
                          event.currentTarget.value = formatCpfCnpj(event.currentTarget.value);
                        }}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="contato">Contato</Label>
                      <Input id="contato" name="contato" defaultValue={supplier.contato || ""} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="telefone">Telefone</Label>
                      <Input 
                        id="telefone" 
                        name="telefone" 
                        type="tel"
                        defaultValue={formatPhoneBR(supplier.telefone || "")}
                        maxLength={15}
                        onChange={(event) => {
                          event.currentTarget.value = formatPhoneBR(event.currentTarget.value);
                        }}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="whatsapp">WhatsApp</Label>
                      <Input 
                        id="whatsapp" 
                        name="whatsapp" 
                        type="tel"
                        defaultValue={formatPhoneBR(supplier.whatsapp || "")}
                        maxLength={15}
                        onChange={(event) => {
                          event.currentTarget.value = formatPhoneBR(event.currentTarget.value);
                        }}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">E-mail</Label>
                      <Input id="email" name="email" type="email" defaultValue={supplier.email || ""} />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="endereco_completo">Endereço Completo</Label>
                    <Textarea id="endereco_completo" name="endereco_completo" defaultValue={supplier.endereco_completo || ""} rows={2} />
                  </div>
                  <div className="flex gap-2">
                    <Button type="submit" disabled={updateMutation.isPending}>
                      <Save className="mr-2 h-4 w-4" />
                      Salvar Alterações
                    </Button>
                    <Button type="button" variant="outline" onClick={() => setIsEditing(false)}>
                      Cancelar
                    </Button>
                  </div>
                </form>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Nome</p>
                      <p>{supplier.nome}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">CNPJ/CPF</p>
                      <p>{supplier.cnpj_cpf ? formatCpfCnpj(supplier.cnpj_cpf) : "-"}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Contato</p>
                      <p>{supplier.contato || "-"}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Telefone</p>
                      <p>{supplier.telefone ? formatPhoneBR(supplier.telefone) : "-"}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">WhatsApp</p>
                      <p>{supplier.whatsapp ? formatPhoneBR(supplier.whatsapp) : "-"}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">E-mail</p>
                      <p>{supplier.email || "-"}</p>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Endereço</p>
                    <p>{supplier.endereco_completo || "-"}</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Timeline */}
        <TabsContent value="timeline" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Timeline de Atividades</CardTitle>
            </CardHeader>
            <CardContent>
              {transactions && transactions.length > 0 ? (
                <div className="relative border-l-2 border-muted pl-6 space-y-6">
                  {transactions.map((transaction) => (
                    <div key={transaction.id} className="relative">
                      <div className="absolute -left-[29px] w-4 h-4 rounded-full bg-primary border-4 border-background" />
                      <div className="space-y-1">
                        <p className="text-sm text-muted-foreground">
                          {format(new Date(transaction.created_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                        </p>
                        <p className="font-medium">
                          Despesa registrada
                        </p>
                        <div className="text-sm">
                          <p>Valor: R$ {Number(transaction.valor_liquido).toFixed(2)}</p>
                          <p>Status: {transaction.status}</p>
                          {transaction.descricao && <p className="text-muted-foreground">{transaction.descricao}</p>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-center py-8">Nenhuma atividade registrada</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Analytics */}
        <TabsContent value="analytics" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card>
              <CardHeader>
                <CardTitle>Frequência de Despesas</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{stats?.frequency || 0}</div>
                <p className="text-sm text-muted-foreground">Despesas nos últimos 30 dias</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Total Gasto</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">
                  R$ {stats?.totalSpent.toFixed(2) || "0.00"}
                </div>
                <p className="text-sm text-muted-foreground">Valor total em despesas</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Engajamento</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">
                  {stats?.totalExpenses 
                    ? ((stats.frequency / stats.totalExpenses) * 100).toFixed(0)
                    : 0}%
                </div>
                <p className="text-sm text-muted-foreground">Taxa de atividade recente</p>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle>Categorias de Despesas (Valor)</CardTitle>
              </CardHeader>
              <CardContent>
                {stats?.categories && stats.categories.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={stats.categories}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="category" angle={-45} textAnchor="end" height={100} />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="value" fill="hsl(var(--primary))" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-muted-foreground text-center py-12">Sem dados disponíveis</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Distribuição Semanal</CardTitle>
              </CardHeader>
              <CardContent>
                {stats?.weeklyDistribution && stats.weeklyDistribution.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={stats.weeklyDistribution}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="day" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="value" fill="hsl(var(--secondary))" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-muted-foreground text-center py-12">Sem dados disponíveis</p>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle>Contas Bancárias Utilizadas</CardTitle>
              </CardHeader>
              <CardContent>
                {stats?.bankAccounts && stats.bankAccounts.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={stats.bankAccounts}
                        dataKey="value"
                        nameKey="account"
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        label
                      >
                        {stats.bankAccounts.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-muted-foreground text-center py-12">Sem dados disponíveis</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Status de Pagamentos</CardTitle>
              </CardHeader>
              <CardContent>
                {stats?.paymentStatus && stats.paymentStatus.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={stats.paymentStatus}
                        dataKey="value"
                        nameKey="status"
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        label
                      >
                        {stats.paymentStatus.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-muted-foreground text-center py-12">Sem dados disponíveis</p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

